import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Loader2, Users } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONSTITUENCIES = [
  "Bumula",
  "Kabuchai",
  "Kanduyi",
  "Kimilili",
  "Mt. Elgon",
  "Sirisia",
  "Tongaren",
  "Webuye East",
  "Webuye West",
  "Westlands (Nairobi)",
  "Other / Diaspora",
];

const ROLES = ["Supporter", "Volunteer", "Youth Mobilizer", "Women's League", "PWD Advocate"];

const inputCls =
  "w-full rounded-xl bg-white/[0.04] border border-white/12 px-4 py-3.5 text-sm text-stone-100 placeholder:text-stone-600 outline-none focus:border-orange-500/70 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200";

export const SupportForm = () => {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    county: "",
    role: "Supporter",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/supporters/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/supporters`, form);
      const r = await axios.get(`${API}/supporters/count`);
      setCount(r.data.count);
      setStatus("success");
      toast.success("Karibu kwa timu! Your support has been recorded.");
    } catch {
      setStatus("idle");
      toast.error("Could not save your signup. Please try again.");
    }
  };

  return (
    <section id="join" data-testid="support-section" className="py-24 sm:py-36 bg-[#12141C]/60 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <Reveal>
            <p className="eyebrow mb-4" data-testid="support-eyebrow">Join the Movement</p>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
              Stand with Tim. <span className="text-orange-500">Bungoma deserves more.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base sm:text-lg text-stone-400 leading-relaxed">
              Register as a supporter or volunteer and be part of the team taking
              experienced, inclusive leadership home to Bungoma County in 2027.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div
              data-testid="supporter-count"
              className="mt-10 inline-flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5"
            >
              <span className="w-11 h-11 rounded-xl bg-orange-600/15 border border-orange-500/40 flex items-center justify-center">
                <Users size={20} className="text-orange-500" />
              </span>
              <div>
                <p className="font-display font-extrabold text-2xl text-stone-100">
                  {count === null ? "—" : count.toLocaleString()}
                </p>
                <p className="mono text-[11px] tracking-[0.18em] uppercase text-stone-500">
                  Supporters registered here
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="glass rounded-3xl p-6 sm:p-9" data-testid="support-form-card">
            {status === "success" ? (
              <div className="text-center py-14" data-testid="supporter-success-badge">
                <BadgeCheck size={52} className="text-orange-500 mx-auto" />
                <h3 className="font-display font-bold text-2xl mt-6">Asante sana, {form.name.split(" ")[0]}!</h3>
                <p className="text-stone-400 text-sm mt-3 max-w-sm mx-auto leading-relaxed">
                  You're now on the Bungoma 2027 team as a {form.role.toLowerCase()} from{" "}
                  {form.county}. Tuko pamoja — we are together.
                </p>
                <button
                  data-testid="supporter-add-another-btn"
                  onClick={() => {
                    setForm({ name: "", contact: "", county: "", role: "Supporter", message: "" });
                    setStatus("idle");
                  }}
                  className="mt-8 rounded-full border border-white/20 hover:border-orange-500/60 hover:text-orange-400 px-6 py-3 mono text-xs tracking-[0.15em] uppercase text-stone-300 transition-colors"
                >
                  Add another supporter
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="support-form">
                <div>
                  <label htmlFor="supporter-name" className="mono text-[11px] tracking-[0.18em] uppercase text-stone-500 block mb-2">
                    Full name
                  </label>
                  <input
                    id="supporter-name"
                    data-testid="supporter-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    placeholder="e.g. Jane Nanjala"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="supporter-contact" className="mono text-[11px] tracking-[0.18em] uppercase text-stone-500 block mb-2">
                    Phone or email
                  </label>
                  <input
                    id="supporter-contact"
                    data-testid="supporter-contact-input"
                    required
                    minLength={5}
                    value={form.contact}
                    onChange={set("contact")}
                    placeholder="07XX XXX XXX or you@email.com"
                    className={inputCls}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="supporter-county" className="mono text-[11px] tracking-[0.18em] uppercase text-stone-500 block mb-2">
                      County
                    </label>
                    <select
                      id="supporter-county"
                      data-testid="supporter-county-select"
                      required
                      value={form.county}
                      onChange={set("county")}
                      className={`${inputCls} appearance-none ${form.county ? "" : "text-stone-600"}`}
                    >
                      <option value="" disabled>
                        Select county
                      </option>
                      {CONSTITUENCIES.map((c) => (
                        <option key={c} value={c} className="bg-[#12141C] text-stone-100">
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="supporter-role" className="mono text-[11px] tracking-[0.18em] uppercase text-stone-500 block mb-2">
                      I want to join as
                    </label>
                    <select
                      id="supporter-role"
                      data-testid="supporter-role-select"
                      value={form.role}
                      onChange={set("role")}
                      className={`${inputCls} appearance-none`}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r} className="bg-[#12141C] text-stone-100">
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="supporter-message" className="mono text-[11px] tracking-[0.18em] uppercase text-stone-500 block mb-2">
                    Message (optional)
                  </label>
                  <textarea
                    id="supporter-message"
                    data-testid="supporter-message-input"
                    rows={3}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Why you're backing Tim for Bungoma…"
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="supporter-submit-btn"
                  disabled={status === "loading"}
                  className="w-full rounded-full bg-orange-600 hover:bg-orange-500 disabled:opacity-60 px-7 py-4 mono text-xs font-semibold tracking-[0.18em] uppercase text-[#0B0C10] transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={15} className="animate-spin" /> Recording…
                    </>
                  ) : (
                    "Count Me In"
                  )}
                </button>
                <p className="mono text-[10px] tracking-[0.14em] uppercase text-stone-600 text-center">
                  Demo site — signups are stored for demonstration only.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
